import React, { useState, useEffect, useCallback } from 'react';
import API from '../../services/api';

const HRModule = () => {
    const [tab, setTab] = useState('directory'); 
    const [employees, setEmployees] = useState([]);
    const [attendanceList, setAttendanceList] = useState([]);
    const [payrollList, setPayrollList] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
    const [stats, setStats] = useState({ active: 0, total: 0 });
    
    const [formData, setFormData] = useState({ 
        full_name: '', cnic: '', username: '', password: '', email: '', contact: '', designation: '', salary: '', status: 1, note: 'Initial onboarding' 
    });

    const fetchData = useCallback(async () => {
        try {
            if (tab === 'directory') {
                const res = await API.get('/v1/hr/employees');
                if (res.data.success) {
                    setEmployees(res.data.data);
                    setStats(res.data.stats);
                }
            } else if (tab === 'attendance') {
                const res = await API.get(`/v1/hr/monthly-attendance?date=${selectedDate}`);
                if (res.data.success) setAttendanceList(res.data.data);
            } else if (tab === 'payroll') {
                const res = await API.get('/v1/hr/payroll');
                if (res.data.success) setPayrollList(res.data.data);
            }
        } catch (err) { 
            console.error("API Error:", err.message); 
        }
    }, [tab, selectedDate]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCreateEmployee = async (e) => {
        e.preventDefault();
        try {
            const res = await API.post('/v1/hr/employees', formData);
            if (res.data.success) {
                alert('Employee added successfully!');
                setShowAddModal(false);
                fetchData();
                setFormData({ full_name: '', cnic: '', username: '', password: '', email: '', contact: '', designation: '', salary: '', status: 1, note: 'Initial onboarding' });
            }
        } catch (err) { alert('Failed to add employee'); }
    };

    const handleStatusChangePrompt = async (emp) => {
        const newStatusInput = prompt("Enter status (1 for Active, 2 for Resigned, 3 for Terminated):", emp.status);
        if (newStatusInput === null) return;
        const noteInput = prompt("Enter remarks/note:", emp.note || '');

        let statusVal = parseInt(newStatusInput);
        try {
            await API.put(`/v1/hr/employees/${emp.id}/status`, { 
                status: statusVal === 1 ? 1 : 0, 
                note: noteInput,
                date_separated: statusVal !== 1 ? new Date().toISOString().slice(0,10) : null
            });
            fetchData();
        } catch (err) { alert('Failed to update status'); }
    };

    const handleStatusToggleClick = async (emp) => {
        const statuses = ['Present', 'Absent', 'Half-Day', 'Leave'];
        const currentIdx = statuses.indexOf(emp.status || 'Present');
        const nextStatus = statuses[(currentIdx + 1) % statuses.length];

        try {
            await API.post('/v1/hr/attendance', {
                employee_id: emp.employee_id,
                date: selectedDate,
                status: nextStatus,
                time_in: nextStatus === 'Present' ? '09:00 AM' : '01:00 PM',
                remarks: `Marked as ${nextStatus}`
            });
            fetchData();
        } catch (err) {
            alert('Failed to update attendance');
        }
    };

    const handleMarkAllPresent = async () => {
        try {
            for (let emp of attendanceList) {
                await API.post('/v1/hr/attendance', {
                    employee_id: emp.employee_id,
                    date: selectedDate,
                    status: 'Present',
                    time_in: '09:00 AM',
                    remarks: 'Regular shift'
                });
            }
            fetchData();
            alert('All staff marked Present successfully!');
        } catch (err) { alert('Error marking attendance'); }
    };

    const presentCount = attendanceList.filter(a => a.status === 'Present').length;
    const attendanceRate = attendanceList.length > 0 ? Math.round((presentCount / attendanceList.length) * 100) : 0;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2>HR & Attendance Module</h2>
                <div style={styles.navTabs}>
                    <button onClick={() => setTab('directory')} style={tab === 'directory' ? styles.activeTab : styles.tabBtn}>Employees Directory</button>
                    <button onClick={() => setTab('attendance')} style={tab === 'attendance' ? styles.activeTab : styles.tabBtn}>Daily Attendance Register</button>
                    <button onClick={() => setTab('payroll')} style={tab === 'payroll' ? styles.activeTab : styles.tabBtn}>Monthly Payroll Summary</button>
                </div>
            </div>

            {/* TAB 1: EMPLOYEES DIRECTORY */}
            {tab === 'directory' && (
                <div>
                    <div style={{display:'flex', justifyContent:'space-between', marginBottom:'20px'}}>
                        <input placeholder="Search by Name, CNIC, or Designation..." style={styles.search} />
                        <button onClick={() => setShowAddModal(true)} style={styles.primaryBtn}>+ Add New Employee</button>
                    </div>
                    <div style={styles.grid}>
                        {employees.map(emp => {
                            const badgeColor = emp.status === 1 ? '#dcfce7' : '#fee2e2';
                            const textColor = emp.status === 1 ? '#166534' : '#991b1b';
                            const statusText = emp.status === 1 ? 'Active' : 'Resigned/Terminated';
                            return (
                                <div key={emp.id} style={styles.card}>
                                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                        <h3 style={{margin:0}}>{emp.full_name}</h3>
                                        <span style={{padding:'3px 10px', borderRadius:'12px', fontSize:'11px', fontWeight:'bold', background:badgeColor, color:textColor}}>
                                            {statusText}
                                        </span>
                                    </div>
                                    <p style={{color:'#64748b', fontSize:'13px', marginTop:'4px'}}>{emp.designation || 'Staff'}</p>
                                    <hr style={{borderColor:'#f1f5f9', margin:'10px 0'}}/>
                                    <div style={{fontSize:'13px', display:'grid', gap:'4px', color:'#334155'}}>
                                        <p style={{margin:0}}><b>CNIC:</b> {emp.cnic}</p>
                                        <p style={{margin:0}}><b>Phone:</b> {emp.contact || 'N/A'}</p>
                                        <p style={{margin:0}}><b>Salary:</b> PKR {Number(emp.salary).toLocaleString()}</p>
                                        <p style={{margin:0, color:'#64748b'}}><b>Note:</b> "{emp.note || 'None'}"</p>
                                    </div>
                                    <button onClick={() => handleStatusChangePrompt(emp)} style={styles.secondaryBtn}>Change Status</button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* TAB 2: DAILY ATTENDANCE REGISTER */}
            {tab === 'attendance' && (
                <div style={styles.tableCard}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px'}}>
                        <div>
                            <label style={{fontSize:'13px', marginRight:'10px', fontWeight:'bold'}}>Attendance Date:</label>
                            <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={styles.inputDate} />
                        </div>
                        <button onClick={handleMarkAllPresent} style={styles.primaryBtn}>Mark All Present</button>
                    </div>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.thTr}>
                                <th>Employee Name</th>
                                <th>Designation</th>
                                <th>Scheduled Shift</th>
                                <th>Status (Click to toggle)</th>
                                <th>Time-In</th>
                                <th>Late Minutes</th>
                                <th>Overtime</th>
                                <th>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attendanceList.map((att, idx) => {
                                const isPresent = att.status === 'Present';
                                const rowBg = isPresent ? '#e0f2fe' : '#ffe4e6';
                                return (
                                    <tr key={idx} style={{...styles.tr, background: rowBg}}>
                                        <td><b>{att.full_name}</b></td>
                                        <td>{att.designation}</td>
                                        <td>09:00 AM - 07:00 PM</td>
                                        <td>
                                            <button onClick={() => handleStatusToggleClick(att)} style={{background:'transparent', border:'none', cursor:'pointer', fontWeight:'bold', color: isPresent ? '#0369a1' : '#be123c'}}>
                                                {att.status}
                                            </button>
                                        </td>
                                        <td>{att.time_in}</td>
                                        <td>{att.late_minutes}</td>
                                        <td>{att.overtime_hours}</td>
                                        <td>{att.remarks}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div style={styles.footerStats}>
                        <div>ACTIVE STAFF <br/><b>{stats.active}</b></div>
                        <div style={{borderLeft:'1px solid #cbd5e1', paddingLeft:'40px'}}>ATTENDANCE RATE <br/><b style={{color:'#0284c7'}}>{attendanceRate}%</b></div>
                    </div>
                </div>
            )}

            {/* TAB 3: MONTHLY PAYROLL SUMMARY */}
            {tab === 'payroll' && (
                <div style={styles.tableCard}>
                    <h3 style={{marginTop:0, marginBottom:'20px'}}>Monthly Payroll Summary</h3>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.thTr}>
                                <th>Employee Name</th>
                                <th>Designation</th>
                                <th>Basic Salary</th>
                                <th>Working Days</th>
                                <th>Days Present</th>
                                <th>Late Deductions</th>
                                <th>Overtime Additions</th>
                                <th>Net Payable Salary</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payrollList.map((p, idx) => (
                                <tr key={idx} style={{...styles.tr, background: idx % 2 === 0 ? '#e0f2fe' : '#fff'}}>
                                    <td><b>{p.full_name}</b></td>
                                    <td>{p.designation}</td>
                                    <td>PKR {Number(p.basic_salary).toLocaleString()}</td>
                                    <td>{p.working_days} Days</td>
                                    <td>{p.days_present} Days</td>
                                    <td><span style={{color:'#dc2626'}}>- PKR {p.deductions.toLocaleString()}</span></td>
                                    <td><span style={{color:'#16a34a'}}>+ PKR {p.overtime_additions.toLocaleString()}</span></td>
                                    <td><b>PKR {Number(p.net_salary).toLocaleString()}</b></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ADD EMPLOYEE MODAL */}
            {showAddModal && (
                <div style={styles.modalOverlay}>
                    <form onSubmit={handleCreateEmployee} style={styles.modal}>
                        <h3>Add New Employee</h3>
                        <input placeholder="Full Name" required value={formData.full_name} onChange={e=>setFormData({...formData, full_name:e.target.value})} style={styles.input}/>
                        <input placeholder="CNIC (e.g. 42101-1234567-1)" required value={formData.cnic} onChange={e=>setFormData({...formData, cnic:e.target.value})} style={styles.input}/>
                        <input placeholder="Username" required value={formData.username} onChange={e=>setFormData({...formData, username:e.target.value})} style={styles.input}/>
                        <input type="password" placeholder="Password" required value={formData.password} onChange={e=>setFormData({...formData, password:e.target.value})} style={styles.input}/>
                        <input placeholder="Designation" value={formData.designation} onChange={e=>setFormData({...formData, designation:e.target.value})} style={styles.input}/>
                        <input placeholder="Email" value={formData.email} onChange={e=>setFormData({...formData, email:e.target.value})} style={styles.input}/>
                        <input placeholder="Contact Number" value={formData.contact} onChange={e=>setFormData({...formData, contact:e.target.value})} style={styles.input}/>
                        <input type="number" placeholder="Basic Salary (PKR)" required value={formData.salary} onChange={e=>setFormData({...formData, salary:e.target.value})} style={styles.input}/>
                        <div style={{display:'flex', gap:'10px', marginTop:'20px'}}>
                            <button type="submit" style={styles.primaryBtn}>Save Employee</button>
                            <button type="button" onClick={()=>setShowAddModal(false)} style={styles.secondaryBtn}>Cancel</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: { padding: '30px', background: '#f8fafc', minHeight: '100vh', fontFamily: 'Segoe UI, sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
    navTabs: { display: 'flex', gap: '10px' },
    tabBtn: { padding: '10px 20px', background: '#e2e8f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', color: '#475569' },
    activeTab: { padding: '10px 20px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
    search: { padding: '10px 14px', width: '350px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' },
    card: { background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
    primaryBtn: { background: '#0284c7', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
    secondaryBtn: { background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '8px', borderRadius: '6px', cursor: 'pointer', width: '100%', marginTop: '15px', fontWeight: 'bold', color: '#334155' },
    tableCard: { background: '#fff', borderRadius: '12px', padding: '25px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '10px' },
    thTr: { background: '#f1f5f9', textAlign: 'left', padding: '14px', borderBottom: '2px solid #cbd5e1', color: '#1e293b', fontSize: '13px' },
    tr: { borderBottom: '1px solid #f1f5f9', padding: '14px', fontSize: '14px', color: '#0f172a' },
    inputDate: { padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' },
    footerStats: { display: 'flex', justifyContent: 'center', gap: '60px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #cbd5e1', textAlign: 'center', fontSize: '14px', color: '#475569' },
    modalOverlay: { position: 'fixed', top:0, left:0, right:0, bottom:0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center' },
    modal: { background: '#fff', padding: '30px', borderRadius: '12px', width: '420px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
    input: { width: '100%', padding: '10px 12px', marginTop: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box', outline: 'none' }
};

export default HRModule;